import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { HeartHandshake, Download, Receipt } from "lucide-react";

function formatINR(n) { return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`; }
function formatDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminDonations() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, s] = await Promise.all([
        api.get("/admin/donations"),
        api.get("/admin/donations/summary"),
      ]);
      setRows(data || []);
      setSummary(s.data || { count: 0, total: 0 });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const exportCsv = () => {
    const headers = ["Date", "Receipt No.", "Donor", "Phone", "Email", "PAN", "Amount", "Method", "Payment ID"];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        formatDT(r.created_at), r.receipt_no || "", r.donor_name || "", r.donor_phone || "",
        r.donor_email || "", r.donor_pan || "", r.amount || 0, r.method || "", r.razorpay_payment_id || "",
      ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `donations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadReceipt = (id) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/donations/${id}/receipt`, "_blank");
  };

  return (
    <div data-testid="admin-donations">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Business · Donations (80G)</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Donations.</h1>
        </div>
        <button onClick={exportCsv} data-testid="don-export"
          className="border border-white/20 px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-white/6 flex items-center gap-2">
          <Download size={14} /> Export CSV (for FY reporting)
        </button>
      </div>

      <div className="bg-[#131A2B] border border-brand-orange/50 p-6 mb-8" data-testid="don-summary">
        <div className="flex items-center gap-4 flex-wrap">
          <HeartHandshake className="brand-orange" size={40} />
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Total Donations Received</div>
            <div className="font-display text-4xl font-black">{formatINR(summary.total)}</div>
            <div className="text-xs text-white/50 mt-1">{summary.count} donor{summary.count !== 1 ? "s" : ""}</div>
          </div>
        </div>
      </div>

      <div className="bg-[#131A2B] border border-white/12 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/12 text-[10px] font-mono uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Receipt No.</th>
              <th className="text-left px-4 py-3">Donor</th>
              <th className="text-left px-4 py-3">PAN</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Method</th>
              <th className="text-left px-4 py-3">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-white/40">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-white/40">No donations yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`don-row-${r.id}`} className="border-b border-white/6 hover:bg-white/4">
                <td className="px-4 py-3 text-white/70 text-xs">{formatDT(r.created_at)}</td>
                <td className="px-4 py-3 font-mono text-xs brand-blue">{r.receipt_no || "—"}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.donor_name || "—"}</div>
                  <div className="text-[11px] text-white/40">{r.donor_phone || ""} {r.donor_email ? "· " + r.donor_email : ""}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-white/70">{r.donor_pan || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(r.amount)}</td>
                <td className="px-4 py-3 text-white/70 text-xs">{r.method || "—"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => downloadReceipt(r.id)} data-testid={`don-receipt-${r.id}`}
                    className="text-xs font-mono uppercase brand-orange hover:underline flex items-center gap-1">
                    <Receipt size={12} /> Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
